"use client"

import ShowPelatihan from "@/components/pelatihan/show-pelatihan"
import PelatihanSearchBar from "@/components/pelatihan/pelatihan-search-bar"
import { useRouter } from "next/navigation"
import { Suspense } from "react"
import Link from "next/link"

export default function PelatihanPage({ searchParams }) {
  const isTargetSelected = !!searchParams?.target;

  return (
    <section className="bg-slate-50 min-h-screen">
      {!isTargetSelected ? (
        <>
          {/* Hero Section */}
          <div className="relative min-h-[500px] bg-slate-950 overflow-hidden flex flex-col pt-10">
            {/* Animated background dots/grid */}
            <div className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, rgba(255,255,255,0.4) 1px, transparent 0)`,
            backgroundSize: `40px 40px`
          }}
        />
        {/* Glow effects */}
        <div className="absolute top-0 right-1/4 w-[500px] h-[500px] bg-blue-600/20 rounded-full blur-[120px] mix-blend-screen animate-pulse pointer-events-none" />
        <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-cyan-500/20 rounded-full blur-[100px] mix-blend-screen animate-pulse delay-700 pointer-events-none" />
        <div className="absolute top-1/2 left-1/2 w-full h-full bg-gradient-to-b from-transparent via-blue-950/50 to-slate-950 z-0 pointer-events-none" />

        <div className="relative z-10 flex flex-col items-center justify-center flex-1 px-4 pt-20 pb-20 text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-white/5 backdrop-blur-md border border-white/10 rounded-full px-5 py-2 mb-8 shadow-lg">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-cyan-500"></span>
            </span>
            <span className="text-cyan-50 text-xs sm:text-sm font-medium tracking-widest uppercase">Portal Pelatihan &amp; Diklat Terpadu</span>
          </div>

          <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold text-white leading-tight mb-6 tracking-tight drop-shadow-2xl">
            Transformasi <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 via-blue-300 to-cyan-400">Kompetensi</span><br /> Masa Depan Anda
          </h1>

          <p className="text-blue-100/90 text-lg md:text-xl max-w-3xl mb-16 leading-relaxed font-light">
            Akses program pelatihan terbaik untuk meningkatkan profesionalisme di sektor Kelautan dan Perikanan Indonesia dengan standar mutu terjamin.
          </p>

          {/* Category Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 w-full max-w-4xl relative z-20">
            {/* Diklat Aparatur */}
            <Link href="?target=aparatur#pelatihan-list" className="group block focus:outline-none focus:ring-4 focus:ring-cyan-500/50 rounded-[2.5rem]">
              <div className="bg-white/5 backdrop-blur-2xl border border-white/10 rounded-[2.5rem] p-10 hover:bg-white/10 hover:border-white/30 hover:shadow-[0_20px_60px_-15px_rgba(59,130,246,0.3)] transition-all duration-500 transform hover:-translate-y-2 cursor-pointer h-full flex flex-col items-center text-center relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/0 via-blue-500/5 to-blue-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-3xl flex items-center justify-center shadow-lg shadow-blue-500/30 group-hover:scale-110 group-hover:rotate-3 transition-transform duration-500 mb-8 z-10">
                  <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <div className="z-10 flex-1 flex flex-col">
                  <h3 className="text-3xl font-bold text-white mb-4">Diklat Aparatur</h3>
                  <p className="text-blue-100/80 text-base leading-relaxed flex-1">
                    Program pengembangan kompetensi strategis bagi{" "}
                    <span className="text-cyan-300 font-semibold">PNS &amp; CPNS</span>{" "}
                    lingkungan KKP.
                  </p>
                  <div className="flex items-center justify-center gap-2 text-cyan-400 font-bold text-sm tracking-widest uppercase mt-8 group-hover:gap-4 transition-all duration-300">
                    <span>Mulai Sekarang</span>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </div>
                </div>
              </div>
            </Link>

            {/* Peserta Umum */}
            <Link href="?target=umum#pelatihan-list" className="group block focus:outline-none focus:ring-4 focus:ring-cyan-500/50 rounded-[2.5rem]">
              <div className="bg-white/5 backdrop-blur-2xl border border-white/10 rounded-[2.5rem] p-10 hover:bg-white/10 hover:border-white/30 hover:shadow-[0_20px_60px_-15px_rgba(34,211,238,0.3)] transition-all duration-500 transform hover:-translate-y-2 cursor-pointer h-full flex flex-col items-center text-center relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/0 via-cyan-500/5 to-cyan-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="w-24 h-24 bg-gradient-to-br from-cyan-400 to-teal-500 rounded-3xl flex items-center justify-center shadow-lg shadow-cyan-400/30 group-hover:scale-110 group-hover:-rotate-3 transition-transform duration-500 mb-8 z-10">
                  <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
                <div className="z-10 flex-1 flex flex-col">
                  <h3 className="text-3xl font-bold text-white mb-4">Peserta Umum</h3>
                  <p className="text-blue-100/80 text-base leading-relaxed flex-1">
                    Pelatihan teknis &amp; keterampilan masyarakat bersertifikat untuk{" "}
                    <span className="text-cyan-300 font-semibold">kemandirian bangsa</span>.
                  </p>
                  <div className="flex items-center justify-center gap-2 text-cyan-400 font-bold text-sm tracking-widest uppercase mt-8 group-hover:gap-4 transition-all duration-300">
                    <span>Lihat Program</span>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </div>
                </div>
              </div>
            </Link>
          </div>

          {/* Moved Search Bar */}
          <div className="w-full flex justify-center mt-6">
            <PelatihanSearchBar />
          </div>
        </div>

        {/* Wave bottom connecting to slate-50 */}
        <div className="absolute bottom-[-1px] left-0 right-0 z-10 pointer-events-none">
          <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full text-slate-50">
            <path d="M0,32L80,42.7C160,53,320,75,480,74.7C640,75,800,53,960,42.7C1120,32,1280,32,1360,32L1440,32L1440,80L1360,80C1280,80,1120,80,960,80C800,80,640,80,480,80C320,80,160,80,80,80L0,80Z" fill="currentColor"></path>
          </svg>
        </div>
          </div>
        </>
      ) : (
        <>
          {/* Pelatihan List View */}
          <div id="pelatihan-list" className="max-w-screen-xl mx-auto space-y-5 md:space-y-8 pt-32 px-4 relative z-20 pb-20">
          <div className="relative group/back mb-4">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-2xl blur opacity-20 group-hover/back:opacity-40 transition duration-500"></div>
            <Link 
              href="/pelatihan" 
              className="relative inline-flex items-center gap-3 px-6 py-3 bg-white/90 backdrop-blur-xl border border-white shadow-[0_8px_30px_rgb(0,0,0,0.04)] rounded-2xl hover:bg-gradient-to-r hover:from-blue-600 hover:to-indigo-600 hover:text-white transition-all duration-300 text-slate-700 font-bold group"
            >
              <div className="bg-slate-100 group-hover:bg-white/20 p-1.5 rounded-lg transition-colors">
                <svg className="w-4 h-4 transform group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M15 19l-7-7 7-7" />
                </svg>
              </div>
              <span className="tracking-wide">Kembali ke Kategori</span>
            </Link>
          </div>

          <Suspense fallback={<div className="text-center font-bold text-lg text-slate-500 py-20 w-full animate-pulse">Memuat Data Pelatihan...</div>}>
            <ShowPelatihan searchParams={searchParams} key={searchParams.target} />
          </Suspense>
          </div>
        </>
      )}
    </section>
  )
}
